// Single source of truth for whether the anonymous (pre-auth) Intercom live-chat
// launcher is actually up, so the login screen can decide whether to show its
// static "Help & support" fallback bubble. IntercomMessenger owns the Intercom
// widget lifecycle and is the ONLY writer here; the login page is a read-only
// subscriber.
//
//   "pending"     - anonymous boot in progress, outcome unknown (show nothing yet)
//   "active"      - launcher booted successfully (hide the static fallback)
//   "unavailable" - Intercom is off, OR the widget failed to load (show fallback)
//
// Keying the fallback on the real boot OUTCOME — not merely the server's
// "configured" flag — guarantees the login screen always keeps exactly one help
// affordance: when a configured widget fails to load (e.g. its CDN/CSP origin is
// blocked while the kloow API host is reachable), the status is "unavailable" and
// the static bubble still appears, so the user is never left with no help at all.

const ANON_INTERCOM_EVENT = 'kloow:anon-intercom-status';

const VALID_STATUSES = new Set(['pending', 'active', 'unavailable']);

let anonIntercomStatus = 'pending';

export function setAnonIntercomStatus(status) {
  const next = VALID_STATUSES.has(status) ? status : 'pending';

  if (next === anonIntercomStatus) {
    return;
  }

  anonIntercomStatus = next;

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(ANON_INTERCOM_EVENT, { detail: { status: next } }));
  }
}

export function getAnonIntercomStatus() {
  return anonIntercomStatus;
}

export function subscribeAnonIntercomStatus(listener) {
  if (typeof window === 'undefined') {
    return () => {};
  }

  const handler = (event) => {
    listener(event?.detail?.status || getAnonIntercomStatus());
  };

  window.addEventListener(ANON_INTERCOM_EVENT, handler);
  return () => window.removeEventListener(ANON_INTERCOM_EVENT, handler);
}
